import {
  Component,
  ChangeDetectionStrategy,
  input,
  output,
  signal,
  forwardRef,
  viewChild,
} from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { AutocompleteFieldComponent } from '../autocomplete-field/autocomplete-field.component';
import { SearchCountry } from '../../../pages/donate/donate-form/donate-form.model';

type DisplayCountry = SearchCountry & { hasSeparatorAfter?: boolean };

const COUNTRY_SELECTOR_VALUE_ACCESSOR = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => CountrySelectorComponent),
  multi: true,
};

@Component({
  selector: 'app-country-selector',
  standalone: true,
  imports: [AutocompleteFieldComponent],
  providers: [COUNTRY_SELECTOR_VALUE_ACCESSOR],
  templateUrl: './country-selector.component.html',
  styleUrl: './country-selector.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CountrySelectorComponent implements ControlValueAccessor {
  // Inputs
  countries = input<SearchCountry[]>([]);
  required = input<boolean>(false);
  hideRequiredInfo = input<boolean>(false);
  label = input<string>('contribute.section1.country.label');
  placeholder = input<string>('contribute.section1.country.placeholder');
  errorMessage = input<string>('contribute.section1.country.error');
  helperText = input<string | null>(null);

  // Outputs
  countrySelect = output<SearchCountry>();
  hasNoValue = output<boolean>();

  // Internal state
  filteredCountries = signal<DisplayCountry[]>([]);
  autocompleteField = viewChild<AutocompleteFieldComponent<SearchCountry>>(AutocompleteFieldComponent);

  // ControlValueAccessor callbacks
  private onChange: (value: SearchCountry | null) => void = () => {};
  private onTouched: () => void = () => {};

  // ControlValueAccessor implementation
  writeValue(value: SearchCountry | null): void {
    const field = this.autocompleteField();
    if (field) {
      field.writeValue(value);
    }
  }

  registerOnChange(fn: (value: SearchCountry | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    // Forward to inner autocomplete field (handled by formControl binding)
  }

  private readonly priorityNames = ['Switzerland', 'United States', 'United Kingdom', 'Australia'];

  private withSeparator(results: SearchCountry[]): DisplayCountry[] {
    const priority = results.filter(c => this.priorityNames.includes(c.name));
    const rest = results.filter(c => !this.priorityNames.includes(c.name));
    if (priority.length === 0 || rest.length === 0) {
      return [...results];
    }
    return [
      ...priority.slice(0, -1),
      { ...priority[priority.length - 1], hasSeparatorAfter: true },
      ...rest,
    ];
  }

  // Event handlers
  onSearch(query: string): void {
    const searchTerm = query.toLowerCase();
    const matched = this.countries().filter(c =>
      c.name.toLowerCase().includes(searchTerm)
    );
    this.filteredCountries.set(this.withSeparator(matched));
  }

  onSelect(country: DisplayCountry): void {
    this.onChange(country);
    this.countrySelect.emit(country);
  }

  onClear(): void {
    this.onChange(null);
    this.hasNoValue.emit(true);
  }
}
